import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import type { CustomFieldComponentProps } from '@/admin/common/EditMemberModal';
import { ReferenceListField } from '@/admin/common/references';
import type { ReferenceListValue } from '@/admin/common/references';
import { useGetModulesByCourse } from '@/common/api/endpoints/modules/modules';

const catalogIdsFromResponse = (response: unknown): Set<string> => {
  const payload = response as { data?: { data?: Array<{ id?: string }> } } | undefined;
  return new Set(
    (payload?.data?.data ?? [])
      .map((module) => module.id)
      .filter((id): id is string => !!id),
  );
};

const EnrollmentExtraModulesField: React.FC<CustomFieldComponentProps<ReferenceListValue | null>> = ({
  value,
  onChange,
  parentId,
}) => {
  const { t } = useTranslation();
  const selected = value ?? [];

  const catalogQuery = useGetModulesByCourse(parentId ?? null, undefined, {
    query: { enabled: !!parentId },
  });

  const catalogIds = useMemo(
    () => catalogIdsFromResponse(catalogQuery.data),
    [catalogQuery.data],
  );

  return (
    <VStack space="sm" testID="extra-modules-field">
      <Text size="xs" color="$textSubtle">
        {t('admin.course.enrollments.extras.help')}
      </Text>

      {selected.length === 0 ? (
        <Box
          bg="$bgSubtle"
          borderRadius="$md"
          px="$3"
          py="$2"
          testID="extra-modules-empty"
        >
          <Text size="sm" color="$textSubtle">
            {t('admin.course.enrollments.extras.empty')}
          </Text>
        </Box>
      ) : null}

      <ReferenceListField
        testID="extra-modules-reference-list"
        value={selected}
        onChange={(next) => onChange(next.length > 0 ? next : [])}
        objectName="modules"
        fixedFilter={{ state: 'published' }}
        searchField="title"
        limit={8}
        searchPlaceholderKey="admin.course.enrollments.extras.searchPlaceholder"
        getDisabledReason={(option) => (
          catalogIds.has(option.id)
            ? t('admin.course.enrollments.extras.onCourse')
            : null
        )}
      />
    </VStack>
  );
};

export default EnrollmentExtraModulesField;
